import { useCallback, useEffect, useState } from 'react'
import {
  ClipboardCheck, Check, X, Loader2, Lightbulb, FolderKanban,
  Clock, CheckCircle2, XCircle,
} from 'lucide-react'
import { formatDate } from '@/lib/utils'

interface ReviewProject { id: string; name: string; color: string; createdAt: string; creatorName: string }
interface ReviewIdea { id: string; title: string; category: string; createdAt: string; submitter: string }
interface MineItem { id: string; name?: string; title?: string; color?: string; category?: string; createdAt: string; status: string; reviewedBy?: string | null; reviewedAt?: string | null }

interface ApprovalsData {
  isAdmin: boolean
  department: string | null
  review: { projects: ReviewProject[]; ideas: ReviewIdea[] }
  mine: { projects: MineItem[]; ideas: MineItem[] }
}

function statusBadge(s: string) {
  const approved = s === 'approved' || s === 'implemented'
  const rejected = s === 'rejected'
  if (approved) return { label: 'Disetujui', cls: 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]', Icon: CheckCircle2 }
  if (rejected) return { label: 'Ditolak', cls: 'bg-[#FEE2E2] text-[#DC2626] border-[#FECACA]', Icon: XCircle }
  return { label: 'Menunggu ACC', cls: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]', Icon: Clock }
}

export default function ApprovalsPage() {
  const [data, setData] = useState<ApprovalsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/approvals')
      if (res.ok) setData(await res.json())
    } catch {
      setFeedback({ kind: 'err', msg: 'Gagal memuat data persetujuan' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function act(type: 'project' | 'idea', id: string, action: 'approve' | 'reject') {
    let note: string | null = null
    if (action === 'reject') {
      note = window.prompt('Alasan ditolak (opsional):') // boleh kosong
      if (note === null) return // user batal
    }
    setBusyId(id)
    setFeedback(null)
    try {
      const res = await fetch('/api/approvals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, id, action, note }),
      })
      const d = await res.json().catch(() => null)
      if (!res.ok) throw new Error(d?.error ?? 'Gagal memproses')
      setFeedback({ kind: 'ok', msg: action === 'approve' ? 'Disetujui ✓' : 'Ditolak' })
      await load()
    } catch (err) {
      setFeedback({ kind: 'err', msg: err instanceof Error ? err.message : 'Terjadi kesalahan' })
    } finally {
      setBusyId(null)
    }
  }

  const reviewCount = (data?.review.projects.length ?? 0) + (data?.review.ideas.length ?? 0)
  const mineCount = (data?.mine.projects.length ?? 0) + (data?.mine.ideas.length ?? 0)

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-3" style={{ height: '52px' }}>
          <ClipboardCheck className="w-4 h-4 text-[#6366F1]" />
          <span className="font-display text-[15px] font-bold text-[#1A1916]">Persetujuan</span>
          {data?.isAdmin && data.department && (
            <span className="text-[10px] uppercase tracking-wider font-body font-semibold text-[#6366F1] bg-[#EEF2FF] border border-[#E0E7FF] px-2 py-0.5 rounded-md">
              {data.department}
            </span>
          )}
        </div>
      </header>

      <main className="px-6 py-6 max-w-3xl mx-auto space-y-6">
        {feedback && (
          <div className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border text-xs font-body ${
            feedback.kind === 'ok' ? 'bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D]' : 'bg-[#FEF2F2] border-[#FECACA] text-[#DC2626]'
          }`}>
            {feedback.msg}
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-16 bg-white border border-[#E8E7E3] rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* ── Antrian ACC (admin) ─────────────────────────────────── */}
            {data?.isAdmin && (
              <section>
                <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold mb-3">
                  Menunggu Persetujuan {reviewCount > 0 && <span className="text-[#DC2626]">({reviewCount})</span>}
                </h2>

                {reviewCount === 0 ? (
                  <div className="bg-white border border-[#E8E7E3] rounded-2xl py-10 text-center">
                    <CheckCircle2 className="w-7 h-7 text-[#C0BDB6] mx-auto mb-2" />
                    <p className="text-sm text-[#8A8A85] font-body">Tidak ada pengajuan yang menunggu</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {data.review.projects.map((p) => (
                      <ReviewCard
                        key={p.id} busy={busyId === p.id}
                        icon={<FolderKanban className="w-4 h-4 text-[#6366F1]" />}
                        kind="Project" color={p.color} title={p.name}
                        sub={`Diajukan oleh ${p.creatorName} · ${formatDate(p.createdAt)}`}
                        onApprove={() => act('project', p.id, 'approve')}
                        onReject={() => act('project', p.id, 'reject')}
                      />
                    ))}
                    {data.review.ideas.map((i) => (
                      <ReviewCard
                        key={i.id} busy={busyId === i.id}
                        icon={<Lightbulb className="w-4 h-4 text-[#8B5CF6]" />}
                        kind="Ide" title={i.title}
                        sub={`Diajukan oleh ${i.submitter} · ${i.category} · ${formatDate(i.createdAt)}`}
                        onApprove={() => act('idea', i.id, 'approve')}
                        onReject={() => act('idea', i.id, 'reject')}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ── Pengajuan saya ──────────────────────────────────────── */}
            <section>
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold mb-3">
                Pengajuan Saya
              </h2>
              {mineCount === 0 ? (
                <div className="bg-white border border-[#E8E7E3] rounded-2xl py-10 text-center">
                  <ClipboardCheck className="w-7 h-7 text-[#C0BDB6] mx-auto mb-2" />
                  <p className="text-sm text-[#8A8A85] font-body">Belum ada pengajuan</p>
                  <p className="text-[11px] text-[#C0BDB6] font-body mt-1">Project/ide yang kamu buat akan muncul di sini beserta statusnya</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {data!.mine.projects.map((p) => (
                    <MineCard key={p.id} icon={<FolderKanban className="w-4 h-4 text-[#6366F1]" />} kind="Project"
                      color={p.color} title={p.name ?? ''} status={p.status} reviewedBy={p.reviewedBy} reviewedAt={p.reviewedAt} createdAt={p.createdAt} />
                  ))}
                  {data!.mine.ideas.map((i) => (
                    <MineCard key={i.id} icon={<Lightbulb className="w-4 h-4 text-[#8B5CF6]" />} kind="Ide"
                      title={i.title ?? ''} status={i.status} reviewedBy={i.reviewedBy} reviewedAt={i.reviewedAt} createdAt={i.createdAt} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  )
}

function ReviewCard({ icon, kind, color, title, sub, busy, onApprove, onReject }: {
  icon: React.ReactNode; kind: string; color?: string; title: string; sub: string
  busy: boolean; onApprove: () => void; onReject: () => void
}) {
  return (
    <div className="bg-white border border-[#E8E7E3] rounded-2xl p-4 flex items-center gap-3">
      {color && <span className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: color }} />}
      <div className="flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider font-body font-bold text-[#AEADA8]">{kind}</span>
        </div>
        <p className="text-[14px] font-body font-semibold text-[#1A1916] truncate">{title}</p>
        <p className="text-[11px] text-[#8A8A85] font-body truncate mt-0.5">{sub}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={onApprove} disabled={busy}
          className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg bg-[#16A34A] text-white text-[12px] font-body font-semibold hover:bg-[#15803D] active:scale-[0.98] disabled:opacity-50 transition-all">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} ACC
        </button>
        <button onClick={onReject} disabled={busy}
          className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg bg-[#FEE2E2] text-[#DC2626] text-[12px] font-body font-semibold hover:bg-[#FECACA] active:scale-[0.98] disabled:opacity-50 transition-all">
          <X className="w-3.5 h-3.5" /> Tolak
        </button>
      </div>
    </div>
  )
}

function MineCard({ icon, kind, color, title, status, reviewedBy, reviewedAt, createdAt }: {
  icon: React.ReactNode; kind: string; color?: string; title: string; status: string
  reviewedBy?: string | null; reviewedAt?: string | null; createdAt: string
}) {
  const b = statusBadge(status)
  const resolved = status !== 'pending' && status !== 'new' && status !== 'reviewing'
  return (
    <div className="bg-white border border-[#E8E7E3] rounded-2xl p-4 flex items-center gap-3">
      {color && <span className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: color }} />}
      <div className="flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <span className="text-[9px] uppercase tracking-wider font-body font-bold text-[#AEADA8]">{kind}</span>
        <p className="text-[14px] font-body font-semibold text-[#1A1916] truncate">{title}</p>
        <p className="text-[11px] text-[#8A8A85] font-body truncate mt-0.5">
          {resolved && reviewedBy ? `oleh ${reviewedBy} · ${reviewedAt ? formatDate(reviewedAt) : ''}` : `Diajukan ${formatDate(createdAt)}`}
        </p>
      </div>
      <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-body font-bold px-2.5 py-1 rounded-md border flex-shrink-0 ${b.cls}`}>
        <b.Icon className="w-3 h-3" />{b.label}
      </span>
    </div>
  )
}
